import { getUserContainer } from 'container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { Box, Modal, Tooltip } from '@mui/material'
import { AppTable, Field } from 'components/table'
import { User, UserQuery } from 'modules/users/models/User'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { Query, QueryParam, QueryParamN } from 'common/api/Query'
import { Actions, Pager, Search, SearchValue } from 'components/table/types'
import genericStyle from 'common/utils/generic.module.css'
import iconDelete from 'assets/user-table/delete.svg'
import addPatientIcon from 'assets/user-table/addPatient.svg'
import { ROUTE_USERS_CREATE } from 'routes/routes-constants'
import editIcon from 'assets/user-table/edit.svg'
import { LoggedUserService } from 'modules/users/services/LoggedUserService'
import { CustomModal } from 'components/modal/CustomModal'
import { useSnackbar } from 'notistack'
import { useIsOpenMenuLeftContext } from 'common/utils/isOpenMenuLeftContext'
import { useNavigate } from 'react-router-dom'
import { Pagination } from '../../components/table/Pagination'
import CardComponent from './CardComponent'
import styles from './ExternCard.module.css'
import { getNotificationContainer } from 'container/notification-module'
import { IExternalInvitationService } from 'modules/notifications/services/ExternalInvitationService'
import { EXTERNAL_INVITATION_SERVICE_KEY } from 'modules/notifications'
import { LangType } from 'common/enums/LangType'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<LoggedUserService>(LOGGED_USER_SERVICE_KEY)
const notificationsContainer = getNotificationContainer()
const invitationsService = notificationsContainer.get<IExternalInvitationService>(
  EXTERNAL_INVITATION_SERVICE_KEY
)

export function Table() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const loggedUser = loggedUserService.get()
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [currentUser, setCurrentUser] = useState<User>()
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const { enqueueSnackbar } = useSnackbar()
  const [showCard, setShowCard] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const [language, setLanguage] = useState<LangType>(loggedUser?.language ?? LangType.English)

  const handleCardSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    invitationsService.sendInvitation(inputValue, language).subscribe({
      next: () => {
        enqueueSnackbar(t('changesWereSaved'), { variant: 'success' })
      },
      error: () => {
        enqueueSnackbar(t('anErrorOccured'), { variant: 'error' })
      },
    })
    setInputValue('')
    setShowCard(false)
  }

  const handleSetLanguage = (value: LangType | Number) => {
    setLanguage(Number(value))
  }
  const innerWidth = window.innerWidth
  const [treatmentsPerPage, setTreatmentsPerPage] = useState<number>(10)

  const [searcher, setSearcher] = useState<SearchValue<UserQuery>[]>([
    {
      name: 'firstName',
      label: t('search') + '...',
    },
  ])

  useEffect(() => {
    // Esta función se ejecutará cada vez que cambie `openMenuLeft`
    console.log('componente renderizado')
  }, [openMenuLeft])

  useEffect(() => {
    if (!isLoading) {
      console.log('no esta loading')
      return
    }
    userService
      .getFilteredList(
        new Query({
          query: [...searcherQuery(searcher)],
          pager: { offset: 0, limit: 1000 },
        })
      )
      .subscribe((res) => {
        const professionals = res.items
          .filter((f) => f.roles.includes('med') || f.roles.includes('medExtern'))
          .filter((f) => f.isActive)

        const start = page * treatmentsPerPage
        const end = start + treatmentsPerPage
        const paged = professionals.slice(start, end)

        setUsers(paged)
        setCount(professionals.length)
        setIsLoading(false)
      })
  }, [isLoading, page, treatmentsPerPage])

  useEffect(() => {
    setIsLoading(true)
  }, [searcher])

  const handlePaginationChange = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setTreatmentsPerPage(10)
      return
    }
    setTreatmentsPerPage(Number.parseInt(event.target.value))
  }
  useEffect(() => {
    setIsLoading(true)
    setPager({
      page,
      count,
      handleChangePage: handlePaginationChange,
      rowsPerPage: treatmentsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, treatmentsPerPage])

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const deleteUser = (u: User) => {
    setCurrentUser(u)
    setOpenDeleteModal(true)
    // navigate(ROUTE_PATIENTS)
  }

  const handleDeleteUser = () => {
    console.log('current user', currentUser)
    if (!currentUser) return
    userService.updateUserToInactiveByID(currentUser.id).subscribe((res) => {
      console.log(res)
      setIsLoading(!isLoading)
      setOpenDeleteModal(false)
      enqueueSnackbar(t('userDeleted'), { variant: 'success' })
    })
  }

  const fields: Field<User>[] = [
    {
      name: 'firstName',
      label: t('fullName'),
      renderFunc: (f, i) => i.firstName + ' ' + i.lastName,
    },
    {
      name: 'email',
      label: t('email'),
    },
    {
      name: 'roles',
      label: t('type'),
      renderFunc: (f, i) => {
        return i.roles.includes('medExtern') ? t('externalProfessional') : t('professional')
      },
    },
  ]

  const search: Search<UserQuery> = {
    searchValues: searcher,
    handleSearch: (svs: SearchValue<UserQuery>[]) => setSearcher(svs),
  }

  const handlerAddUser = () => navigate(ROUTE_USERS_CREATE, { state: { isProfessional: true } })
  const editUser = (u: User) =>
    navigate(`${ROUTE_USERS_CREATE}/${u.id}`, { state: { isProfessional: true } })

  const isAdmin = loggedUser?.roles.includes('admin') || false

  const actions: Actions<User> = {
    actionsColumn: t('Actions'),
    items: [
      {
        handler: editUser,
        icon: editIcon,
        label: t('edit'),
        hidden: (u) => !isAdmin,
      },

      {
        handler: deleteUser,
        icon: iconDelete,
        label: t('delete'),
        hidden: (u) => !isAdmin,
      },
    ],
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        style={{
          maxWidth:
            innerWidth > 900 ? (openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)') : '95%',
          position: 'relative',
        }}
        className={genericStyle.pageContainer}
      >
        <div className={genericStyle.containerMobile}>
          <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
            <CustomModal
              title={t('deleteProfesional')}
              warningText={t('questionDeleteProfesional')}
              handleClose={handleCloseDeleteModal}
              handleSave={handleDeleteUser}
            />
          </Modal>
          <AppTable
            rowKeyField="id"
            items={users}
            fields={fields}
            actions={actions}
            search={search}
            isLoading={isLoading}
          />
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
          >
            <Box sx={{ marginLeft: openMenuLeft ? '35px' : '10px' }}>
              {pager && <Pagination {...pager} />}
            </Box>
          </Box>
        </div>
        {isAdmin && (
          <>
            <Box
              style={{ position: 'absolute', right: -30, bottom: -3, cursor: 'pointer' }} // Contenedor para posicionar la tarjeta
            >
              <Tooltip title={t('createProfessional')}>
                <img
                  src={addPatientIcon}
                  alt="add professional"
                  width={90}
                  onClick={() => setShowCard(!showCard)}
                />
              </Tooltip>

              {showCard && (
                <div
                  className={styles.cardWrapper}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%) translateY(-135%)',
                  }}
                >
                  <CardComponent
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    onSubmit={handleCardSubmit}
                    handlerAddUser={handlerAddUser}
                    language={language}
                    setLanguage={handleSetLanguage}
                  />
                </div>
              )}
            </Box>
          </>
        )}
      </Box>
    </div>
  )
}

const searcherQuery = (
  svs: SearchValue<UserQuery>[]
): QueryParam<UserQuery>[] | QueryParamN<UserQuery>[] =>
  svs.filter((sv) => sv.value).map((sv) => ({ name: sv.name, value: sv.value as string }))
